import type { Property } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { LocationSlugFilters } from '~~/core/ports/repositories';

import { mergeResolvedLocation } from '~/core/entities/property/filterPolicy';

export const LISTING_PAGE_SIZE = 12;

export interface SearchResult {
	properties: Property[];
	page: number;
	filteredCount: number;
	totalPages: number;
	failure: 'search' | 'page' | null;
}
export interface SearchRequest {
	filters: PropertyFilters;
	page: number;
	mode: 'replace' | 'append';
}
export interface ListingPage {
	properties: Property[];
	filteredCount: number;
	totalPages: number;
	locationSlugFilters?: LocationSlugFilters;
}
export type FetchListingPage = (page: number) => Promise<ListingPage | null>;
export type ListingPagesOutcome =
	| { status: 'cancelled' }
	| { status: 'failed' }
	| { status: 'done'; result: SearchResult; resolvedFilters: PropertyFilters };

export function emptySearchResult(): SearchResult {
	return {
		properties: [],
		page: 1,
		filteredCount: 0,
		totalPages: 1,
		failure: null
	};
}

// "Ver mais" continues after what is shown; any other entry shows only the page it names.
export function pageRange(request: SearchRequest, previous: SearchResult) {
	return request.mode === 'append'
		? { first: previous.page + 1, last: request.page }
		: { first: request.page, last: request.page };
}

export function dedupeById(properties: Property[]): Property[] {
	const seen = new Set<Property['id']>();
	return properties.filter(property => {
		if (seen.has(property.id)) return false;
		seen.add(property.id);
		return true;
	});
}

// A shared ?page=N may outlive the results; show the last existing page instead.
async function fetchPageInRange(fetchPage: FetchListingPage, page: number) {
	const response = await fetchPage(page);
	const lastPage = Math.max(response?.totalPages ?? 1, 1);
	if (!response || page <= lastPage) return { response, page };
	return { response: await fetchPage(lastPage), page: lastPage };
}

export async function fetchListingPages(input: {
	request: SearchRequest;
	previous: SearchResult;
	fetchPage: FetchListingPage;
	isStale: () => boolean;
	singlePage: boolean;
}): Promise<ListingPagesOutcome> {
	const { request, previous } = input;
	const isAppend = request.mode === 'append';
	const collected = isAppend ? [...previous.properties] : [];
	const result = isAppend ? { ...previous } : emptySearchResult();
	let resolvedFilters = request.filters;
	const { first, last } = pageRange(request, previous);
	// Only a direct entry can name a page past the results; "ver mais" never does.
	const fetchPage = isAppend
		? async (page: number) => ({ response: await input.fetchPage(page), page })
		: (page: number) => fetchPageInRange(input.fetchPage, page);
	for (let requested = first; requested <= last; requested++) {
		const { response, page } = await fetchPage(requested);
		if (input.isStale()) return { status: 'cancelled' };
		if (!response) return { status: 'failed' };
		collected.push(...response.properties);
		resolvedFilters = mergeResolvedLocation(
			resolvedFilters,
			response.locationSlugFilters
		);
		Object.assign(result, {
			page,
			filteredCount: response.filteredCount,
			totalPages: response.totalPages
		});
		if (page >= response.totalPages || input.singlePage) break;
	}
	return {
		status: 'done',
		result: { ...result, properties: dedupeById(collected), failure: null },
		resolvedFilters
	};
}
